/**
 * Vertellis Gift With Purchase
 *
 * Pairs a gift selector (rendered by snippets/gwp-gift-selector.liquid) with
 * the theme's product form. When the customer submits the form with a gift
 * selected, trigger product + gift are added in a single /cart/add call and
 * the theme's normal post-add flow (cartUpdate event, Opus cart drawer) is
 * replayed.
 *
 * Pricing is intentionally NOT handled here: an automatic Buy X Get Y
 * discount in Shopify admin is the only source of truth for the gift being
 * free. This script only orchestrates the add-to-cart UX.
 */
(function () {
  'use strict';

  if (customElements.get('gwp-gift-selector')) return;

  const selectors = new Set();

  class GwpGiftSelector extends HTMLElement {
    connectedCallback() {
      try {
        this.config = JSON.parse(this.querySelector('[data-gwp-config]').textContent);
      } catch (error) {
        console.warn('[GWP] Invalid config, selector disabled.', error);
        return;
      }

      this.errorElement = this.querySelector('.gwp__error');
      this.liveElement = this.querySelector('.gwp__live');
      this.inputs = Array.from(this.querySelectorAll('.gwp-card__input'));

      this.inputs.forEach((input) => {
        input.addEventListener('change', () => this.onGiftChange(input));
      });
      // Reflect any server-preselected state (free mode preselects the first gift).
      this.syncCardState(null, { silent: true });

      selectors.add(this);
      this.dispatchAnalytics('gwp:viewed', {});
    }

    disconnectedCallback() {
      selectors.delete(this);
    }

    /** All chosen gift inputs. Radio (free) mode yields 0-1; checkbox (discount) mode yields 0-N. */
    get selectedGifts() {
      return this.inputs.filter((input) => input.checked && !input.disabled);
    }

    onGiftChange(input) {
      this.syncCardState(input);
      this.hideError();
      this.dispatchAnalytics('gwp:gift-selected', {
        gift_product_id: Number(input.dataset.giftProductId),
        gift_variant_id: Number(input.value)
      });
    }

    syncCardState(changed, options = {}) {
      // Works for radios (one checked) and checkboxes (many): each card reflects its own input.
      this.inputs.forEach((input) => {
        input.closest('.gwp-card').classList.toggle('is-selected', input.checked);
      });
      if (!options.silent && changed && changed.checked && this.liveElement) {
        this.liveElement.textContent = this.config.strings.selectedAnnouncement.replace(
          '[gift]',
          changed.dataset.giftTitle
        );
      }
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    hideError() {
      if (this.errorElement) this.errorElement.hidden = true;
    }

    dispatchAnalytics(name, detail) {
      document.dispatchEvent(
        new CustomEvent(name, {
          detail: Object.assign(
            {
              campaign_id: this.config.campaignId,
              trigger_product_id: this.config.triggerProductId
            },
            detail
          )
        })
      );
    }

    /** Whether the given add-to-cart form is buying this campaign's trigger product. */
    matchesForm(form) {
      const idField = form.elements.namedItem('id');
      if (!idField) return false;
      const variantId = Number(idField.value);
      return this.config.triggerVariantIds.indexOf(variantId) !== -1;
    }
  }

  customElements.define('gwp-gift-selector', GwpGiftSelector);

  /**
   * Intercept the add-to-cart action at document capture phase so we run
   * before both the theme's submit handler and cart apps (e.g. Opus) that
   * take over the buy button's click. The click listener is the primary
   * hook; the submit listener covers keyboard-submitted forms.
   */
  function handleAddToCartIntent(event, form, submitButton) {
    const selector = findSelectorForForm(form);
    if (!selector) return;

    if (submitButton && submitButton.hasAttribute('aria-disabled')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const gifts = selector.selectedGifts;

    if (!gifts.length) {
      if (selector.config.requireSelection) {
        event.preventDefault();
        event.stopPropagation();
        selector.showError(selector.config.strings.selectGift);
      }
      // Nothing selected and not required (always the case in discount mode):
      // let the native flow add the trigger product on its own.
      return;
    }

    if (form.dataset.gwpBusy === 'true') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    form.dataset.gwpBusy = 'true';

    event.preventDefault();
    event.stopPropagation();
    selector.hideError();
    submitCombinedAdd(form, selector, gifts, submitButton).finally(() => {
      delete form.dataset.gwpBusy;
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest('button[type="submit"], button[name="add"]');
      if (!button) return;
      const form = button.form;
      if (!form || !form.matches('form[data-type="add-to-cart-form"]')) return;
      handleAddToCartIntent(event, form, button);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;
      handleAddToCartIntent(event, form, form.querySelector('[type="submit"]'));
    },
    true
  );

  function findSelectorForForm(form) {
    for (const selector of selectors) {
      if (selector.config && selector.matchesForm(form)) return selector;
    }
    return null;
  }

  function setLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('btn--loading', isLoading);
    if (isLoading) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  }

  function collectSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  function buildItems(form, selector, gifts) {
    const formData = new FormData(form);
    const triggerItem = {
      id: Number(formData.get('id')),
      quantity: Number(formData.get('quantity')) || 1
    };
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) triggerItem.selling_plan = Number(sellingPlan);

    const triggerProperties = {};
    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) triggerProperties[match[1]] = value;
    });
    if (Object.keys(triggerProperties).length) triggerItem.properties = triggerProperties;

    const giftItems = gifts.map((gift) => ({
      id: Number(gift.value),
      quantity: 1,
      properties: {
        _gwp: 'true',
        _campaign_id: selector.config.campaignId,
        _trigger_product_id: String(selector.config.triggerProductId)
      }
    }));

    return [triggerItem].concat(giftItems);
  }

  async function addItems(items, sections) {
    const response = await fetch(FoxTheme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items,
        sections: sections.join(','),
        sections_url: window.location.pathname
      })
    });
    return response.json();
  }

  /** Replays ProductForm's post-add flow: cart state, events, drawer. */
  async function finalizeAdd(parsedState) {
    const cartJson = await (
      await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })
    ).json();
    cartJson.sections = parsedState.sections;
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
    document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));
    if (typeof window.opusOpen === 'function') {
      window.opusOpen();
    } else {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.show();
    }
  }

  async function submitCombinedAdd(form, selector, gifts, submitButton) {
    setLoading(submitButton, true);
    const sections = collectSections();
    const items = buildItems(form, selector, gifts);

    try {
      let parsedState = await addItems(items, sections);

      if (parsedState.status) {
        // Combined add failed (usually a gift went unavailable between
        // render and submit). Retry with the trigger product only so the
        // main purchase never silently fails, then tell the customer.
        parsedState = await addItems([items[0]], sections);
        if (parsedState.status) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'gwp',
            productVariantId: items[0].id,
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message
          });
          selector.showError(parsedState.description || parsedState.message);
          return;
        }
        await finalizeAdd(parsedState);
        selector.showError(selector.config.strings.giftAddFailed);
        return;
      }

      selector.dispatchAnalytics('gwp:added-to-cart', {
        gift_variant_ids: gifts.map((gift) => Number(gift.value))
      });
      await finalizeAdd(parsedState);
    } catch (error) {
      console.error('[GWP] Add to cart failed', error);
      selector.showError(selector.config.strings.giftAddFailed);
    } finally {
      setLoading(submitButton, false);
    }
  }
})();
